/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

// Shared accessibility helpers for the Map Viewer.
//
// The Map Viewer has four dialog-shaped surfaces -- the point side panel, the
// detached point popup, the image lightbox and the cookie consent round popup
// -- which were each written separately and had between zero and one of the
// behaviours a dialog owes a keyboard user. This module is the single
// implementation they all delegate to.

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

let liveRegion = null;
let liveRegionTimer = null;

/**
 * Report whether an element is hidden from the tab order.
 *
 * Deliberately attribute-driven rather than geometry-driven: `offsetParent`
 * and `getClientRects()` report every element as invisible under jsdom, so a
 * geometric test would filter out everything the unit tests set up. The
 * computed-style checks below are the ones jsdom does implement.
 */
function isHidden(element) {
    if (element.hasAttribute('hidden') || element.closest('[inert]')) {
        return true;
    }
    if (element.closest('[aria-hidden="true"]')) {
        return true;
    }
    const style = window.getComputedStyle?.(element);
    if (!style) {
        return false;
    }
    return style.visibility === 'hidden' || style.display === 'none';
}

export const a11y = {
    FOCUSABLE_SELECTOR,

    /**
     * Collect the focusable descendants of `root`, in tab order.
     *
     * Queries the whole of `root`. A trap that queries only the content
     * wrapper silently drops any control mounted as a sibling of it -- which
     * is how the consent dialog's close button ended up outside its own tab
     * cycle.
     */
    getFocusable(root, options = {}) {
        if (!root || typeof root.querySelectorAll !== 'function') {
            return [];
        }
        const found = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR));
        if (options.includeRoot && root.matches?.(FOCUSABLE_SELECTOR)) {
            found.unshift(root);
        }
        return found.filter((element) => !isHidden(element));
    },

    /**
     * Confine Tab to `container` until the returned release function is
     * called.
     *
     * @param {Element} container the dialog root, close button included
     * @param {object} [options]
     * @param {Element} [options.initialFocus] what to focus on open
     * @param {Element|null} [options.returnFocus] what to focus on release;
     *     defaults to whatever was focused when the trap was installed
     * @param {Function} [options.onEscape] called instead of closing, so the
     *     caller decides what Escape means
     * @param {boolean} [options.modal] also mark the rest of the page inert
     * @returns {Function} release
     */
    trapFocus(container, options = {}) {
        if (!container) {
            return () => {};
        }
        const previous =
            options.returnFocus === undefined
                ? document.activeElement
                : options.returnFocus;
        const releaseIsolation = options.modal
            ? a11y.isolate(container)
            : null;

        const onKeydown = (event) => {
            if (event.key === 'Escape' && options.onEscape) {
                // The map binds its own window-level Escape handler to close
                // an embed; stop this one reaching it.
                event.stopPropagation();
                options.onEscape(event);
                return;
            }
            if (event.key !== 'Tab') {
                return;
            }
            const focusable = a11y.getFocusable(container);
            const active = document.activeElement;
            if (!focusable.length) {
                event.preventDefault();
                container.focus?.();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const escaped = !container.contains(active);
            if (event.shiftKey) {
                if (escaped || active === first || active === container) {
                    event.preventDefault();
                    last.focus();
                }
            } else if (escaped || active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeydown, true);

        // First control by default. Callers that want the screen reader to
        // read the dialog from the top -- the point panel, which is a
        // document rather than a form -- pass the container as initialFocus.
        const target =
            options.initialFocus ||
            container.querySelector('[autofocus]') ||
            a11y.getFocusable(container)[0] ||
            container;
        target?.focus?.();

        let released = false;
        return function release() {
            if (released) {
                return;
            }
            released = true;
            document.removeEventListener('keydown', onKeydown, true);
            releaseIsolation?.();
            if (previous) {
                a11y.restoreFocus(previous);
            }
        };
    },

    /**
     * Hide everything outside `keepEl` from assistive technology.
     *
     * Walks up to `root`, marking each level's other children inert. Needed
     * for the detached point popup, which renders *inside* the map model: a
     * focus trap alone still leaves the nodes underneath in the screen
     * reader's virtual buffer.
     *
     * @returns {Function} release
     */
    isolate(keepEl, root = document.body) {
        const touched = [];
        let node = keepEl;
        while (node && node !== root && node.parentElement) {
            const parent = node.parentElement;
            for (const sibling of Array.from(parent.children)) {
                if (sibling === node || sibling.hasAttribute('inert')) {
                    continue;
                }
                sibling.setAttribute('inert', '');
                sibling.setAttribute('aria-hidden', 'true');
                touched.push(sibling);
            }
            node = parent;
        }
        return function release() {
            for (const element of touched) {
                element.removeAttribute('inert');
                element.removeAttribute('aria-hidden');
            }
            touched.length = 0;
        };
    },

    /**
     * Move focus back to `element`, falling back when it has gone away.
     *
     * Map nodes are removed from the tab order when they leave the current
     * zoom range, so the element a dialog was opened from is not necessarily
     * still focusable by the time it closes.
     */
    restoreFocus(element, fallback) {
        const candidate =
            element?.isConnected && !isHidden(element) ? element : fallback;
        if (!candidate || typeof candidate.focus !== 'function') {
            return false;
        }
        try {
            candidate.focus();
            return true;
        } catch (err) {
            console.error('Failed to restore focus:', err);
            return false;
        }
    },

    /**
     * Announce `message` through a shared live region.
     */
    announce(message, options = {}) {
        if (!message) {
            return;
        }
        // `isConnected` matters: the region is cached across calls, and a
        // cached node that has been detached from the document announces
        // nothing at all -- silently, since nothing throws.
        if (!liveRegion || !liveRegion.isConnected) {
            liveRegion = document.createElement('div');
            liveRegion.className = 'sr-only';
            liveRegion.setAttribute('role', 'status');
            document.body.appendChild(liveRegion);
        }
        liveRegion.setAttribute(
            'aria-live',
            options.assertive ? 'assertive' : 'polite'
        );
        // Re-setting identical text is not announced again, so clear first.
        liveRegion.textContent = '';
        window.clearTimeout(liveRegionTimer);
        liveRegionTimer = window.setTimeout(() => {
            liveRegion.textContent = message;
        }, 50);
    },

    /**
     * Report whether the viewer has asked for reduced motion.
     *
     * The CSS half of this lives in default.css; this is for motion that
     * JavaScript starts, which no stylesheet can reach.
     */
    prefersReducedMotion() {
        return (
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ===
            true
        );
    },

    /**
     * Report whether `event` is the keyboard equivalent of a click.
     */
    isActivationKey(event) {
        return event.key === 'Enter' || event.key === ' ';
    },

    /**
     * Make a non-button element operable by keyboard as well as pointer.
     *
     * Prefer a real `<button>`. This exists for the map nodes, which d3
     * creates as `div`s and which cannot be re-tagged without reworking the
     * renderer.
     */
    onActivate(element, handler) {
        element.addEventListener('click', handler);
        element.addEventListener('keydown', (event) => {
            if (!a11y.isActivationKey(event)) {
                return;
            }
            // Space scrolls the page and Enter would submit a surrounding
            // form; neither is what activating a map point should do.
            event.preventDefault();
            handler(event);
        });
    },

    /**
     * Build a visually hidden label element.
     */
    srOnly(text) {
        const span = document.createElement('span');
        span.className = 'sr-only';
        span.textContent = text;
        return span;
    }
};

export default a11y;
